import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fetchUsers, updateUserPermission } from "../redux/actions/authActions";
import { fetchRelationships } from "../redux/actions/relationshipActions";
import { register } from "../redux/actions/authActions";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Container,
  Box,
  Tabs,
  Tab,
  Paper,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { useSnackbar } from "notistack";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import WorkIcon from "@mui/icons-material/Work";
import GroupIcon from "@mui/icons-material/Group";
import PhoneIcon from "@mui/icons-material/Phone";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { actionButtonStyles } from "../styles/actionButton";
import { windowsTableStyles } from "../styles/windowsTableStyles";
import { textFieldStyles } from "../styles/textFieldStyles";
import BreadcrumbNav from "../components/BreadcrumbNav";
import { CustomPaper } from "../styles/customPaper";
import { mediumTitle, smallBold } from "../styles/typography";
import { verticalTabStyles } from "../styles/verticalTabStyles";
import axios from "../utils/axiosConfig";

const UserManagement = () => {
  const { t } = useTranslation(["pages", "common", "buttons", "forms"]);
  const [tabIndex, setTabIndex] = useState(0);
  const tableStyles = windowsTableStyles();
  const dispatch = useDispatch();
  const { users, loading, error } = useSelector((state) => state.userList);
  const { relationships } = useSelector((state) => state.relationshipList);
  const { enqueueSnackbar } = useSnackbar();
  const { isAuthenticated, error: authError } = useSelector(
    (state) => state.userLoaded
  );

  // Add state for edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "",
    phone: "",
    isActive: true,
  });
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    password: "",
    role: "",
    relationship: "",
    keepLoggedIn: false,
    avatar: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [notificationShown, setNotificationShown] = useState(true);

  // Filter and search state
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const {
    firstName,
    middleName,
    lastName,
    email,
    password,
    role,
    relationship,
    keepLoggedIn,
    avatar,
  } = formData;

  useEffect(() => {
    dispatch(fetchUsers());
    dispatch(fetchRelationships());
  }, [dispatch]);

  const handlePermissionToggle = (id, isActive) => {
    dispatch(updateUserPermission(id, !isActive));
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    const avatar = generateAvatar(firstName, lastName);
    dispatch(
      register({
        firstName,
        middleName,
        lastName,
        email,
        password,
        role,
        relationship,
        keepLoggedIn,
        avatar,
      })
    );
    setNotificationShown(false);
  };

  useEffect(() => {
    if (isAuthenticated && !notificationShown) {
      enqueueSnackbar(t("pages:users.user_created"), { variant: "success" });
      setNotificationShown(true);
    }
    if (authError && !notificationShown) {
      enqueueSnackbar(authError, { variant: "error" });
      setNotificationShown(true);
    }
  }, [isAuthenticated, authError, enqueueSnackbar, notificationShown]);

  const handleRelationshipChange = (event, newValue) => {
    if (newValue) {
      setFormData({
        ...formData,
        firstName: newValue.firstName,
        middleName: newValue.middleName || "",
        lastName: newValue.lastName,
        email: newValue.email,
        relationship: newValue._id,
      });
    } else {
      setFormData({
        ...formData,
        firstName: "",
        middleName: "",
        lastName: "",
        email: "",
        relationship: "",
      });
    }
  };

  const generateAvatar = (firstName, lastName) => {
    const colors = [
      "#FAD7A0",
      "#AED6F1",
      "#ABEBC6",
      "#F5B7B1",
      "#F9E79F",
      "#D7BDE2",
    ];
    const bgColor = colors[Math.floor(Math.random() * colors.length)];
    const initials = `${firstName[0]}${lastName[0]}`;
    const avatarURL = `https://ui-avatars.com/api/?name=${initials}&background=${bgColor.replace(
      "#",
      ""
    )}&color=000&size=128`;
    return avatarURL;
  };

  // Handle opening the edit modal
  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditFormData({
      firstName: user.firstName || "",
      middleName: user.middleName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      password: "",
      confirmPassword: "",
      role: user.role || "",
      phone: user.phone || "",
      isActive: user.isActive,
    });
    setEditModalOpen(true);
  };

  // Handle edit form changes
  const handleEditChange = (e) => {
    setEditFormData({
      ...editFormData,
      [e.target.name]: e.target.value,
    });
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();

    // Validate passwords match if provided
    if (
      editFormData.password &&
      editFormData.password !== editFormData.confirmPassword
    ) {
      enqueueSnackbar(t("forms:validation.passwordsDoNotMatch"), {
        variant: "error",
      });
      return;
    }

    try {
      setUpdating(true);

      // Prepare data for submission (exclude confirmPassword)
      const updateData = { ...editFormData };
      delete updateData.confirmPassword;

      // Only include password if it's not empty
      if (!updateData.password) {
        delete updateData.password;
      }

      const response = await axios.put(
        `/api/users/${selectedUser._id}`,
        updateData
      );

      // Update the user in the Redux store
      dispatch(fetchUsers());

      setEditModalOpen(false);
      enqueueSnackbar(t("pages:users.user_updated"), { variant: "success" });
    } catch (error) {
      console.error("Error updating user:", error);
      enqueueSnackbar(
        error.response?.data?.message || t("pages:users.error_updating"),
        {
          variant: "error",
        }
      );
    } finally {
      setUpdating(false);
    }
  };

  const availableRelationships = relationships?.filter(
    (rel) => !users.some((user) => user.relationship === rel._id)
  );

  const roleOptions = [
    {
      label: t("forms:options.role.assistant_lawyer"),
      value: "Assistant Lawyer",
    },
    {
      label: t("forms:options.role.responsible_lawyer"),
      value: "Responsible Lawyer",
    },
  ];

  const statusOptions = [
    { value: "Active", label: t("common:active") },
    { value: "Inactive", label: t("common:inactive") },
  ];

  // Filter users with search and filters
  const filteredUsers = users?.filter((user) => {
    // Exclude Manager role
    if (user.role === "Manager") return false;

    // Normalize search string
    const normalizeString = (str) => str?.replace(/\s+/g, "").toLowerCase() || "";
    const searchText = normalizeString(search);

    // Search filter - match first name, last name, email, or full name
    const matchesSearch =
      !searchText ||
      normalizeString(user.firstName || "").includes(searchText) ||
      normalizeString(user.lastName || "").includes(searchText) ||
      normalizeString(user.email || "").includes(searchText) ||
      normalizeString(
        `${user.firstName || ""} ${user.middleName || ""} ${user.lastName || ""}`
      ).includes(searchText);

    // Status filter - convert boolean isActive to string for comparison
    const matchesStatusFilter =
      !statusFilter ||
      (statusFilter === "Active" && user.isActive === true) ||
      (statusFilter === "Inactive" && user.isActive === false);

    // Role filter
    const matchesRoleFilter = !roleFilter || user.role === roleFilter;

    return matchesSearch && matchesStatusFilter && matchesRoleFilter;
  }) || [];
  const renderSignupForm = () => (
    <Box>
      <form onSubmit={onSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Autocomplete
              options={availableRelationships?.filter(
                (rel) => rel.type === "Lawyer"
              )}
              getOptionLabel={(option) =>
                `${option.uniqueNumber} - ${option.firstName} ${
                  option.middleName ? `${option.middleName} ` : ""
                }${option.lastName}`
              }
              value={
                availableRelationships?.find(
                  (rel) => rel._id === relationship
                ) || null
              }
              onChange={handleRelationshipChange}
              PaperComponent={CustomPaper}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("forms:labels.relationship")}
                  size="small"
                  sx={{
                    ...textFieldStyles({ marginRight: "0" }),
                  }}
                  margin="normal"
                  required
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <GroupIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid container spacing={2} item xs={12}>
            <Grid item xs={4}>
              <TextField
                margin="normal"
                id="firstName"
                label={t("forms:labels.firstName")}
                name="firstName"
                autoComplete="given-name"
                autoFocus
                value={firstName}
                fullWidth
                onChange={onChange}
                size="small"
                sx={{
                  ...textFieldStyles({ marginRight: "0" }),
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                size="small"
                sx={{
                  ...textFieldStyles({ marginRight: "0" }),
                }}
                fullWidth
                id="middleName"
                label={t("forms:labels.middleName")}
                name="middleName"
                autoComplete="additional-name"
                value={middleName}
                onChange={onChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                required
                fullWidth
                size="small"
                sx={{
                  ...textFieldStyles({ marginRight: "0" }),
                }}
                id="lastName"
                label={t("forms:labels.lastName")}
                name="lastName"
                autoComplete="family-name"
                value={lastName}
                onChange={onChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Grid item xs={4}>
            <TextField
              required
              id="email"
              label={t("forms:labels.emailAddress")}
              name="email"
              autoComplete="email"
              value={email}
              onChange={onChange}
              fullWidth
              size="small"
              sx={{
                ...textFieldStyles({ marginRight: "0" }),
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              required
              fullWidth
              name="password"
              label={t("forms:labels.password")}
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={onChange}
              size="small"
              sx={{
                ...textFieldStyles({ marginRight: "0" }),
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth>
              <Autocomplete
                options={roleOptions}
                getOptionLabel={(option) => option.label}
                value={
                  roleOptions.find((option) => option.value === role) || null
                }
                onChange={(event, newValue) =>
                  onChange({
                    target: {
                      name: "role",
                      value: newValue ? newValue.value : "",
                    },
                  })
                }
                PaperComponent={CustomPaper}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("forms:labels.role")}
                    size="small"
                    sx={{
                      ...textFieldStyles({ marginRight: "0" }),
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <WorkIcon />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
        <Box sx={{ marginTop: 4 }}>
          <Button type="submit" fullWidth sx={actionButtonStyles}>
            {t("pages:users.create_account")}
          </Button>
        </Box>
      </form>
    </Box>
  );

  // Edit User Dialog
  const renderEditUserDialog = () => (
    <Dialog
      open={editModalOpen}
      onClose={() => setEditModalOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{t("pages:users.edit_user")}</DialogTitle>
      <form onSubmit={handleEditSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                margin="normal"
                label={t("forms:labels.firstName")}
                name="firstName"
                value={editFormData.firstName}
                onChange={handleEditChange}
                fullWidth
                size="small"
                sx={{
                  ...textFieldStyles({ marginRight: "0" }),
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                margin="normal"
                label={t("forms:labels.middleName")}
                name="middleName"
                value={editFormData.middleName}
                onChange={handleEditChange}
                fullWidth
                size="small"
                sx={{
                  ...textFieldStyles({ marginRight: "0" }),
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                margin="normal"
                label={t("forms:labels.lastName")}
                name="lastName"
                value={editFormData.lastName}
                onChange={handleEditChange}
                fullWidth
                size="small"
                sx={{
                  ...textFieldStyles({ marginRight: "0" }),
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="normal"
                label={t("forms:labels.emailAddress")}
                name="email"
                value={editFormData.email}
                onChange={handleEditChange}
                fullWidth
                size="small"
                sx={{
                  ...textFieldStyles({ marginRight: "0" }),
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="normal"
                label={t("forms:labels.phone")}
                name="phone"
                value={editFormData.phone}
                onChange={handleEditChange}
                fullWidth
                size="small"
                sx={{
                  ...textFieldStyles({ marginRight: "0" }),
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel
                  id="role-label"
                  sx={{ backgroundColor: "white", px: 0.5 }}
                >
                  {t("forms:labels.role")}
                </InputLabel>
                <Select
                  labelId="role-label"
                  name="role"
                  value={editFormData.role}
                  onChange={handleEditChange}
                  size="small"
                  label={t("forms:labels.role")}
                  sx={{
                    ...textFieldStyles({ marginRight: "0" }),
                  }}
                  startAdornment={
                    <InputAdornment position="start">
                      <WorkIcon />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="Assistant Lawyer">
                    {t("forms:options.role.assistant_lawyer")}
                  </MenuItem>
                  <MenuItem value="Responsible Lawyer">
                    {t("forms:options.role.responsible_lawyer")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel
                  id="status-label"
                  sx={{ backgroundColor: "white", px: 0.5 }}
                >
                  {t("common:status")}
                </InputLabel>
                <Select
                  labelId="status-label"
                  name="isActive"
                  value={editFormData.isActive}
                  onChange={handleEditChange}
                  size="small"
                  label={t("common:status")}
                  sx={{
                    ...textFieldStyles({ marginRight: "0" }),
                  }}
                >
                  <MenuItem value={true}>{t("common:active")}</MenuItem>
                  <MenuItem value={false}>{t("common:inactive")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="normal"
                label={t("forms:labels.newPassword")}
                name="password"
                type={showEditPassword ? "text" : "password"}
                value={editFormData.password}
                onChange={handleEditChange}
                fullWidth
                size="small"
                sx={{
                  ...textFieldStyles({ marginRight: "0" }),
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowEditPassword(!showEditPassword)}
                        edge="end"
                      >
                        {showEditPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="normal"
                label={t("forms:labels.confirmPassword")}
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={editFormData.confirmPassword}
                onChange={handleEditChange}
                fullWidth
                size="small"
                sx={{
                  ...textFieldStyles({ marginRight: "0" }),
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>
            {t("buttons:confirmation.cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={actionButtonStyles}
            disabled={updating}
          >
            {updating ? (
              <CircularProgress size={24} />
            ) : (
              t("buttons:primary.save_changes")
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );

  const renderUserTable = () => (
    <TableContainer>
      <Table sx={tableStyles.container} size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={tableStyles.headerCell}>
              {t("forms:labels.firstName")}
            </TableCell>
            <TableCell sx={tableStyles.headerCell}>
              {t("forms:labels.lastName")}
            </TableCell>
            <TableCell sx={tableStyles.headerCell}>
              {t("forms:labels.email")}
            </TableCell>
            <TableCell sx={tableStyles.headerCell}>
              {t("forms:labels.role")}
            </TableCell>
            <TableCell sx={tableStyles.headerCell}>
              {t("common:status")}
            </TableCell>
            <TableCell sx={tableStyles.headerCell}>
              {t("common:actions")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredUsers
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((user) => (
            <TableRow key={user._id} sx={{ ...tableStyles.row }}>
              <TableCell sx={tableStyles.cell}>{user.firstName}</TableCell>
              <TableCell sx={tableStyles.cell}>{user.lastName}</TableCell>
              <TableCell sx={tableStyles.cell}>{user.email}</TableCell>
              <TableCell sx={tableStyles.cell}>{user.role}</TableCell>
              <TableCell sx={tableStyles.cell}>
                {user.isActive ? t("common:active") : t("common:inactive")}
              </TableCell>
              <TableCell sx={tableStyles.cell}>
                <Button
                  onClick={() => handleEditUser(user)}
                  sx={{
                    ...smallBold,
                    borderRadius: "0",
                    textTransform: "none",
                    transition: "all 0.3s ease",
                    padding: "0px 20px",
                    marginRight: 0.5,
                    color: "#ffffff",
                    backgroundColor: "#4caf50",
                    "&:hover": {
                      boxShadow:
                        "2px 2px 4px rgba(0,0,0,0.1), -2px -2px 4px rgba(255,255,255,0.7)",
                      backgroundColor: "#43a047",
                    },
                  }}
                  size="small"
                >
                  {t("common:edit")}
                </Button>
                <Button
                  onClick={() =>
                    handlePermissionToggle(user._id, user.isActive)
                  }
                  sx={{
                    ...smallBold,
                    borderRadius: "0",
                    textTransform: "none",
                    transition: "all 0.3s ease",
                    padding: "0px 20px",
                    marginRight: 0.5,
                    color: user.isActive ? "#ffffff" : "#2B80B6",
                    backgroundColor: user.isActive ? "#2B80B6" : "#ffffff",
                    "&:hover": {
                      boxShadow:
                        "2px 2px 4px rgba(0,0,0,0.1), -2px -2px 4px rgba(255,255,255,0.7)",
                    },
                  }}
                  size="small"
                >
                  {user.isActive
                    ? t("buttons:secondary.revoke")
                    : t("buttons:secondary.grant")}
                </Button>
              </TableCell>
            </TableRow>
            ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={filteredUsers?.length}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        labelRowsPerPage={t("common:rows_per_page")}
      />
    </TableContainer>
  );

  return (
    <Container
      maxWidth="lg"
      sx={{
        backgroundColor: "white",
        boxShadow: "0px 4px 16px rgba(0,0,0,0.013)",
        pt: 2,
        marginBottom: 8,
        marginTop: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0",
          margin: "0",
          mb: 4,
          mt: 2,
          gap: 2,
        }}
      >
        <BreadcrumbNav title={t("pages:users.title")} />
      </Box>
      <Box sx={{ paddingBottom: 4 }}>
        <Box
          sx={{
            display: "flex",
            backgroundColor: "white",
            marginTop: "24px",
            border: "1px solid #d1d1d1",
          }}
        >
          <Box sx={{ width: "200px", borderRight: "1px solid #d1d1d1" }}>
            <Box sx={{ marginBottom: "40px", marginTop: "30px" }}>
              <Tabs
                value={tabIndex}
                onChange={handleTabChange}
                aria-label="user management tabs"
                orientation="vertical"
                TabIndicatorProps={{
                  sx: {
                    left: 0, // Position the indicator at the start
                  },
                }}
                sx={verticalTabStyles}
              >
                <Tab label={t("common:users")} />
                <Tab label={t("pages:users.create_account")} />
              </Tabs>
            </Box>
          </Box>
          <Box
            sx={{
              flexGrow: 1,

              paddingTop: "48px",
              paddingBottom: "32px",
              padding: 5,
              width: "calc(100% - 200px)",
            }}
          >
            {tabIndex === 0 && (
              <>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        marginBottom: 2,
                      }}
                    >
                      <TextField
                        label={t("common:searchAnything")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                          sx: {
                            padding: "0 10px",
                            boxShadow: "none",
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        sx={textFieldStyles({ width: "25%", marginRight: "0" })}
                        size="small"
                      />
                      <Autocomplete
                        options={statusOptions}
                        getOptionLabel={(option) => option.label || option}
                        value={
                          statusOptions.find((opt) => opt.value === statusFilter) ||
                          null
                        }
                        onChange={(event, newValue) =>
                          setStatusFilter(newValue?.value || "")
                        }
                        sx={{ width: "25%" }}
                        size="small"
                        PaperComponent={CustomPaper}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("pages:relationships.filter_by_status")}
                            sx={textFieldStyles({ width: "100%", marginRight: "0" })}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <FilterListIcon />
                                </InputAdornment>
                              ),
                            }}
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        )}
                      />
                      <Autocomplete
                        options={roleOptions}
                        getOptionLabel={(option) => option.label || option}
                        value={
                          roleOptions.find((opt) => opt.value === roleFilter) || null
                        }
                        onChange={(event, newValue) =>
                          setRoleFilter(newValue?.value || "")
                        }
                        sx={{ width: "25%" }}
                        size="small"
                        PaperComponent={CustomPaper}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("pages:relationships.filter_by")}
                            sx={textFieldStyles({ width: "100%", marginRight: "0" })}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <FilterListIcon />
                                </InputAdornment>
                              ),
                            }}
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Grid>
                </Grid>
                {renderUserTable()}
              </>
            )}
            {tabIndex === 1 && renderSignupForm()}
            {renderEditUserDialog()}
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default UserManagement;
